import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Paper,
  Alert
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import MDBox from 'components/MDBox';
import { useAuth } from 'auth/Auth';

export const storageEnvironment = process.env.REACT_APP_STORAGE_ENV || '';
async function requestPresignedUpload({
  axiosInstance,
  fileName,
  fileType,
  featurePath,
  scope = 'user',
  includeDatePartition = true
}) {
  const response = await axiosInstance.post('/attachments/presign-upload', {
    file_name: fileName,
    file_type: fileType || 'application/octet-stream',
    feature_path: featurePath,
    scope,
    include_date_partition: includeDatePartition
  });
  return response.data;
}

// Retrieve attachments

export async function fetchS3Attachments({axiosInstance, parent_id, parent_table, updateAttachments}) {
  try {
    const response = await axiosInstance.get('/attachments/', {
      params: { parent_table: parent_table, parent_id: parent_id },
    });
    updateAttachments(response.data || []);
  } catch (error) {
    console.error('Error fetching attachments:', error);
  }
}

// Get image attachment

export const getImageAttachment = (attachments) => {
  const imageAttachments = (attachments || []).filter(
      (a) => a.file_type && a.file_type.startsWith('image/')
  );
  if (!imageAttachments.length) return null;
  return [...imageAttachments].sort((a, b) => b.id - a.id)[0];
};

export const getImageURL = async (attachments, axiosInstance) => {
  const attachment = getImageAttachment(attachments);
  if (!attachment) {
    return null
  }

  try {
    if (!attachment.bucket_name || !attachment.storage_path) {
      throw new Error('Attachment storage metadata is invalid for strict R2 download');
    }

    return await previewPrivateObject({ axiosInstance, attachmentId: attachment.id });

  } catch (error) {
    console.error('Error previewing attachment:', error);
  }
}

export const deleteImageObject = async (axiosInstance, linkedAttachment, updateAttachments) => {
  try {
    await axiosInstance.delete(`/attachments/${linkedAttachment.id}`);
    updateAttachments((prev) => prev.filter((a) => a.id !== linkedAttachment.id));
  } catch (error) {
    console.error('Error deleting attachment:', error);
  }
}

//

export const uploadReplaceImage = async ({
  axiosInstance,
  updateIsAttachmentModalOpen,
  attachments,
  updateAttachments,
  uploadData,
  parent_table,
  parent_id,
}) => {

  updateIsAttachmentModalOpen(false);

  const linkedAttachment = getImageAttachment(attachments);

  // Replace: delete the old image attachment
  if (linkedAttachment) {
    await deleteImageObject(axiosInstance, linkedAttachment, updateAttachments);
  }

  const uploadDataPayload = {
    file_name: uploadData.file.name,
    file_type: uploadData.file.type,
    file_size: uploadData.file.size,
    bucket_name: uploadData.bucket,
    storage_path: uploadData.path,
    description: uploadData.description || '',
    parent_table: parent_table,
    parent_id: parent_id,
    is_active: true,
    is_private: uploadData.isPrivate || false,
    file_url: uploadData.url,
    signed_url_expiry: uploadData.signedUrlExpiry || null,
  };

  try {
    await axiosInstance.post('/attachments/', uploadDataPayload);
  } catch (error) {
    console.error('Error uploading file:', error);
  } finally {
    fetchS3Attachments({
      axiosInstance: axiosInstance,
      parent_id: parent_id,
      parent_table: parent_table,
      updateAttachments: updateAttachments,
    });
  }

  return await getImageURL(attachments, axiosInstance);
};


export async function uploadPrivateObject({
  axiosInstance,
  file,
  env,
  featurePath,
  scope = 'user',
  includeDatePartition = false,
}) {
  if (!file) {
    throw new Error('No file provided');
  }
  if (!axiosInstance) {
    throw new Error('axiosInstance is required');
  }
  if (!featurePath) {
    throw new Error('featurePath is required');
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('feature_path', featurePath);
  formData.append('scope', scope);
  formData.append('include_date_partition', String(includeDatePartition));
  const response = await axiosInstance.post('/attachments/proxy-upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  const uploadData = response.data;

  return {
    bucket: uploadData.bucket_name,
    path: uploadData.storage_path,
    url: uploadData.download_url,
    contentType: uploadData.file_type || file.type || 'application/octet-stream',
    size: uploadData.file_size || file.size || 0
  };
}

export async function uploadPrivateObjectsBulk({
  axiosInstance,
  files,
  featurePath,
  scope = 'user',
  includeDatePartition = false,
}) {
  const orderedFiles = Array.from(files || []);
  if (!orderedFiles.length) {
    throw new Error('At least one file is required');
  }
  if (!axiosInstance) {
    throw new Error('axiosInstance is required');
  }
  if (!featurePath) {
    throw new Error('featurePath is required');
  }

  const formData = new FormData();
  orderedFiles.forEach((file) => formData.append('files', file, file.name));
  formData.append('feature_path', featurePath);
  formData.append('scope', scope);
  formData.append('include_date_partition', String(includeDatePartition));

  const response = await axiosInstance.post(
    '/attachments/proxy-upload-bulk',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );

  return (response.data || []).map((uploadData, index) => ({
    bucket: uploadData.bucket_name,
    path: uploadData.storage_path,
    url: uploadData.download_url,
    fileName: uploadData.file_name || orderedFiles[index]?.name,
    contentType:
      uploadData.file_type ||
      orderedFiles[index]?.type ||
      'application/octet-stream',
    size: uploadData.file_size || orderedFiles[index]?.size || 0
  }));
}

export async function uploadPublicObject({
  axiosInstance,
  file,
  featurePath,
  includeDatePartition = true,
}) {
  if (!file) {
    throw new Error('No file provided');
  }
  const [uploaded] = await uploadPublicObjectsBulk({
    axiosInstance,
    files: [file],
    featurePath,
    includeDatePartition,
  });
  if (!uploaded) {
    throw new Error('Public image upload returned an empty result');
  }
  return uploaded;
}

export async function uploadPublicObjectsBulk({
  axiosInstance,
  files,
  featurePath,
  includeDatePartition = true,
}) {
  const orderedFiles = Array.from(files || []);
  if (!orderedFiles.length) {
    throw new Error('At least one file is required');
  }
  if (!axiosInstance) {
    throw new Error('axiosInstance is required');
  }
  if (!featurePath) {
    throw new Error('featurePath is required');
  }

  const formData = new FormData();
  orderedFiles.forEach((file) => formData.append('files', file, file.name));
  formData.append('feature_path', featurePath);
  formData.append('include_date_partition', String(includeDatePartition));

  const response = await axiosInstance.post(
    '/attachments/proxy-upload-bulk-public',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );

  return (response.data || []).map((uploadData, index) => ({
    bucket: uploadData.bucket_name,
    path: uploadData.storage_path,
    url: uploadData.download_url,
    fileName: uploadData.file_name || orderedFiles[index]?.name,
    contentType:
      uploadData.file_type ||
      orderedFiles[index]?.type ||
      'application/octet-stream',
    size: uploadData.file_size || orderedFiles[index]?.size || 0
  }));
}


export async function previewPrivateObject({
  axiosInstance,
  attachmentId,
}) {
  if (!axiosInstance || !attachmentId) {
    throw new Error('axiosInstance and attachmentId are required for private download');
  }
  const presignResponse = await axiosInstance.get(`/attachments/${attachmentId}/presign-download`);
  const signedUrl = presignResponse.data?.download_url;
  if (!signedUrl) {
    throw new Error('No download URL returned for attachment');
  }
  // Prefer a blob URL when CORS allows; otherwise use the signed URL for <img src>.
  try {
    const fileResponse = await fetch(signedUrl);
    if (fileResponse.ok) {
      const blob = await fileResponse.blob();
      return URL.createObjectURL(blob);
    }
  } catch (_) {
    // R2 CORS can block browser fetch; signed URLs still work in image tags.
  }
  return signedUrl;
}

export async function downloadPrivateObject({
  axiosInstance,
  attachmentId,
  fileName,
}) {
  if (!axiosInstance || !attachmentId) {
    throw new Error('axiosInstance and attachmentId are required for private download');
  }
  const presignResponse = await axiosInstance.get(`/attachments/${attachmentId}/presign-download`);
  const signedUrl = presignResponse.data.download_url;
  const fileResponse = await fetch(signedUrl);
  if (!fileResponse.ok) {
    throw new Error(`Download failed with status ${fileResponse.status}`);
  }
  const blob = await fileResponse.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName || `attachment-${attachmentId}`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export async function deletePrivateObject({ bucketName, key }) {
  return { bucketName, key };
}

/**
 * PUTs the raw file to a presigned URL. Uses XMLHttpRequest (not fetch) so
 * we get real upload progress events. The Content-Type header sent here
 * MUST match the ContentType used when the URL was signed, or R2 will
 * reject the request with a signature mismatch.
 */
function putFileToPresignedUrl(file, uploadUrl, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const uploadHost = (() => {
      try {
        return new URL(uploadUrl).host;
      } catch {
        return 'invalid-upload-url';
      }
    })();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType || 'application/octet-stream');

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress((event.loaded / event.total) * 100);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status} to ${uploadHost}`));
      }
    };

    xhr.onerror = () => {
      reject(new Error(`Network error during upload to ${uploadHost} (status ${xhr.status})`));
    };

    xhr.send(file);
  });
}

/**
 * Upload one file and return its storage coordinates, reporting progress.
 *
 * Tries the direct route first -- a presigned PUT straight to R2 -- so large
 * files (property videos run to 100 MB) never pass through the API process.
 * If the browser cannot reach R2 directly (bucket CORS not allowing this
 * origin shows up as a network error), falls back to /attachments/proxy-upload,
 * which validates the content server-side and has always worked.
 */
export async function uploadFileWithProgress({
  axiosInstance,
  file,
  featurePath,
  onProgress,
  scope = 'user',
}) {
  if (!file || !axiosInstance || !featurePath) {
    throw new Error('file, axiosInstance and featurePath are required');
  }
  const contentType = file.type || 'application/octet-stream';
  try {
    const presigned = await requestPresignedUpload({
      axiosInstance,
      fileName: file.name,
      fileType: contentType,
      featurePath,
      scope,
    });
    await putFileToPresignedUrl(file, presigned.upload_url, contentType, onProgress);
    return {
      bucket: presigned.bucket_name,
      path: presigned.storage_path,
      url: presigned.download_url,
      contentType,
      size: file.size,
    };
  } catch (directError) {
    if (onProgress) onProgress(0);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('feature_path', featurePath);
    formData.append('scope', scope);
    formData.append('include_date_partition', 'true');
    const response = await axiosInstance.post('/attachments/proxy-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (event) => {
        if (onProgress && event.total) onProgress((event.loaded / event.total) * 100);
      },
    });
    const data = response.data;
    return {
      bucket: data.bucket_name,
      path: data.storage_path,
      url: data.download_url,
      contentType: data.file_type || contentType,
      size: data.file_size || file.size,
    };
  }
}

// Standalone helper for anywhere else in the app that wants to upload
// without mounting the component.
export const uploadDocument = async (
    file,
    featurePath = 'attachments',
    options = {}
) => {
  const uploadResult = await uploadPrivateObject({
    axiosInstance: options.axiosInstance,
    file,
    featurePath,
    scope: options.scope || 'user',
    env: options.env,
    includeDatePartition: options.includeDatePartition
  });

  return {
    path: uploadResult.path,
    url: uploadResult.url,
    bucket: uploadResult.bucket,
    file: {
      name: file.name,
      size: file.size,
      type: file.type
    }
  };
};

// Main component, same prop/ref contract AttachmentUploadModal already
// expects, so that component needs no changes.
const S3FileUpload = React.forwardRef(({
  folderPath = 'uploads',
  scope = 'user',
  allowedFileTypes = '*/*',
  maxSizeInMB = 25,
  onUploadSuccess,
  onUploadError,
  onCancel,
  customFileName,
  onFileSelect,
}, ref) => {
  const { axiosInstance } = useAuth();
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);

  const validateFile = useCallback((file) => {
    if (!file) return { isValid: false, error: 'No file selected' };

    const sizeInMB = file.size / (1024 * 1024);
    if (sizeInMB > maxSizeInMB) {
      return { isValid: false, error: `File size must be less than ${maxSizeInMB}MB` };
    }

    if (allowedFileTypes !== '*/*') {
      const allowedTypes = allowedFileTypes.split(',').map(type => type.trim());
      const isAllowed = allowedTypes.some((type) => {
        try {
          return new RegExp(`^${type.replace(/\*/g, '.*')}$`, 'i').test(file.type || '');
        } catch {
          return false;
        }
      });
      if (!isAllowed) {
        const imagesOnly = allowedTypes.every((t) => t === 'image/*' || t.startsWith('image/'));
        return {
          isValid: false,
          error: imagesOnly
            ? 'Only image files are allowed (JPEG, PNG, GIF, WebP, etc.). PDFs and other documents cannot be uploaded.'
            : `File type must be one of: ${allowedTypes.join(', ')}`,
        };
      }
    }

    return { isValid: true, error: null };
  }, [maxSizeInMB, allowedFileTypes]);

  const applySelectedFile = (file) => {
    const validation = validateFile(file);
    if (!validation.isValid) {
      setError(validation.error);
      return;
    }

    setError(null);
    setSelectedFile(file);
    onFileSelect?.(file);

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    applySelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setIsUploading(true);
      setUploadProgress(0);
      setError(null);
      const uploadResult = await uploadPrivateObject({
        axiosInstance,
        file: selectedFile,
        featurePath: folderPath,
        scope,
        includeDatePartition: true
      });
      setUploadProgress(100);

      onUploadSuccess?.({
        path: uploadResult.path,
        url: uploadResult.url,
        bucket: uploadResult.bucket,
        file: {
          name: selectedFile.name,
          size: selectedFile.size,
          type: selectedFile.type
        }
      });

      setSelectedFile(null);
      setPreview(null);
      setError(null);
    } catch (error) {
      console.error('Upload error:', error);
      setError(error.message || 'Failed to upload file');
      onUploadError?.(error.message || 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  // Expose methods to parent through ref
  React.useImperativeHandle(ref, () => ({
    handleUpload,
    selectedFile: () => selectedFile,
    isUploading: () => isUploading
  }));

  const handleCancel = () => {
    setSelectedFile(null);
    setPreview(null);
    setError(null);
    onCancel?.();
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const file = event.dataTransfer.files[0];
    if (!file) return;
    applySelectedFile(file);
  };

  return (
      <Paper elevation={2} sx={{ p: 3, maxWidth: 500, mx: 'auto' }}>
        {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
        )}

        {!selectedFile ? (
            <Box
                sx={{
                  border: '2px dashed',
                  borderColor: 'grey.300',
                  borderRadius: 1,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  position: 'relative'
                }}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
            >
              <input
                  type="file"
                  accept={allowedFileTypes}
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  id="file-input"
              />
              <label htmlFor="file-input" style={{ width: '100%', height: '100%', cursor: 'pointer' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                  <Typography variant="subtitle1" color="primary" sx={{ mb: 1 }}>
                    Choose a file
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    or drag and drop it here
                  </Typography>
                </Box>
              </label>
            </Box>
        ) : (
            <Box sx={{ mt: 2 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                {preview ? (
                    <Box sx={{ mb: 2 }}>
                      <img
                          src={preview}
                          alt="Preview"
                          style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }}
                      />
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      {selectedFile.type.startsWith('image/') ? <ImageIcon fontSize="large" /> :
                          selectedFile.type.includes('pdf') ? <PictureAsPdfIcon fontSize="large" /> :
                              <InsertDriveFileIcon fontSize="large" />}
                      <Box sx={{ ml: 2 }}>
                        <Typography variant="subtitle2">{selectedFile.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                        </Typography>
                      </Box>
                    </Box>
                )}

                {isUploading ? (
                    <Box sx={{ mt: 2 }}>
                      <LinearProgress variant="determinate" value={uploadProgress} sx={{ mb: 1 }} />
                      <Typography variant="body2" color="text.secondary" align="center">
                        Uploading... {Math.round(uploadProgress)}%
                      </Typography>
                    </Box>
                ) : (
                    <MDBox mt={4} mb={1} display="flex" justifyContent="flex-end">
                    </MDBox>
                )}
              </Paper>
            </Box>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          {allowedFileTypes === '*/*'
            ? 'Allowed files: All files'
            : allowedFileTypes === 'image/*' || allowedFileTypes.split(',').every((t) => t.trim() === 'image/*' || t.trim().startsWith('image/'))
              ? 'Allowed files: Images only (JPEG, PNG, GIF, WebP, etc.) — PDFs are not accepted'
              : `Allowed files: ${allowedFileTypes}`}
          <br />
          {`Maximum size: ${maxSizeInMB}MB`}
        </Typography>
      </Paper>
  );
});

export default S3FileUpload;

// Called once a trip has a real ID (after wizard step 1 saves) — uploads
// the locally-picked banner file and links it via /attachments/.
export async function uploadPendingTripImage({
   axiosInstance,
   file,
   parentId,
   parentTable = 'travel_orders',
   featurePath = 'trip-banners',
}) {
  const uploadResult = await uploadPrivateObject({
    axiosInstance,
    file,
    featurePath,
    scope: 'user',
    includeDatePartition: true,
  });

  const response = await axiosInstance.post('/attachments/', {
    file_name: file.name,
    file_type: file.type,
    file_size: file.size,
    bucket_name: uploadResult.bucket,
    storage_path: uploadResult.path,
    parent_table: parentTable,
    parent_id: parentId,
    is_active: true,
    is_private: false,
    file_url: uploadResult.url,
  });

  return response.data;
}